import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AdminLayout } from '../components/Layout';

interface Client {
    id: number;
    name: string;
}

interface Campaign {
    name: string;
    client_id: number;
    created_at: string;
}

export function AdminCampaigns() {
    const [admin, setAdmin] = useState<any>(null);
    const [clients, setClients] = useState<Client[]>([]);
    const [campaigns, setCampaigns] = useState<Campaign[]>([]);
    const [loading, setLoading] = useState(true);
    const [showAddModal, setShowAddModal] = useState(false);
    const [newCampaign, setNewCampaign] = useState({ name: '', client_id: '' });
    const [actionLoading, setActionLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [editingCampaign, setEditingCampaign] = useState<Campaign | null>(null);
    const [editName, setEditName] = useState('');
    const [editClientId, setEditClientId] = useState<number | string>('');

    const navigate = useNavigate();

    const getApiBase = () => {
        return '/api';
    };

    const request = async (endpoint: string, options: RequestInit = {}) => {
        const response = await fetch(`${getApiBase()}${endpoint}`, {
            ...options,
            credentials: 'include',
            headers: { 'Content-Type': 'application/json', ...options.headers },
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.error || 'Request failed');
        return data;
    };

    useEffect(() => {
        const stored = localStorage.getItem('rewagent_admin');
        if (stored) {
            setAdmin(JSON.parse(stored));
            fetchData();
        } else {
            navigate('/admin');
        }
    }, [navigate]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const [clientsData, campaignsData] = await Promise.all([
                request('/admin/clients'),
                request('/admin/campaigns')
            ]);
            setClients(clientsData);
            setCampaigns(campaignsData);
        } catch (err: any) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleCreateCampaign = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!newCampaign.name || !newCampaign.client_id) return;

        setActionLoading(true);
        setError(null);
        try {
            await request('/admin/campaigns', {
                method: 'POST',
                body: JSON.stringify(newCampaign)
            });
            setShowAddModal(false);
            setNewCampaign({ name: '', client_id: '' });
            fetchData();
        } catch (err: any) {
            setError(err.message);
        } finally {
            setActionLoading(false);
        }
    };

    const handleEditCampaign = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!editingCampaign) return;
        setActionLoading(true);
        setError(null);
        try {
            await request(`/admin/campaigns/${encodeURIComponent(editingCampaign.name)}`, {
                method: 'PUT',
                body: JSON.stringify({
                    new_name: editName !== editingCampaign.name ? editName : undefined,
                    new_client_id: Number(editClientId) !== editingCampaign.client_id ? editClientId : undefined,
                }),
            });
            setEditingCampaign(null);
            fetchData();
        } catch (err: any) {
            setError(err.message);
        } finally {
            setActionLoading(false);
        }
    };

    const handleDeleteCampaign = async (name: string) => {
        if (!confirm(`Delete campaign "${name}"? This only works if it has no leads.`)) return;
        setActionLoading(true);
        try {
            await request(`/admin/campaigns/${encodeURIComponent(name)}`, { method: 'DELETE' });
            fetchData();
        } catch (err: any) {
            alert(err.message || 'Failed to delete campaign');
        } finally {
            setActionLoading(false);
        }
    };

    if (!admin) return null;

    return (
        <AdminLayout
            activeTab="campaigns"
            onTabChange={(tab) => navigate(`/admin/${tab}`)}
            onLogout={() => {
                localStorage.removeItem('rewagent_admin');
                navigate('/admin');
            }}
        >
            <div className="p-8 max-w-7xl mx-auto">
                <div className="flex justify-between items-center mb-8">
                    <div>
                        <h1 className="text-3xl font-bold text-[#1a1a1a]">Campaigns</h1>
                        <p className="text-[#6b635a] mt-1">Manage valid campaign strings for leads.</p>
                    </div>
                    <button
                        onClick={() => setShowAddModal(true)}
                        className="flex items-center gap-2 px-4 py-2 bg-[#1a1a1a] text-[#f3ede3] rounded-lg hover:bg-[#333] transition-colors"
                    >
                        <span>＋</span> Create Campaign
                    </button>
                </div>

                {loading ? (
                    <div className="flex justify-center py-12">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1a1a1a]"></div>
                    </div>
                ) : (
                    <div className="bg-[#fcfbfa] rounded-xl border border-[#e3dbcf] overflow-hidden shadow-sm">
                        <div className="overflow-x-auto">
                            <table className="w-full text-left">
                                <thead className="bg-[#f3ede3]/50 border-b border-[#e3dbcf]">
                                    <tr>
                                        <th className="px-6 py-4 text-xs font-semibold text-[#8c8578] uppercase tracking-wider">Campaign Name</th>
                                        <th className="px-6 py-4 text-xs font-semibold text-[#8c8578] uppercase tracking-wider">Client</th>
                                        <th className="px-6 py-4 text-xs font-semibold text-[#8c8578] uppercase tracking-wider">Created</th>
                                        <th className="px-6 py-4 text-xs font-semibold text-[#8c8578] uppercase tracking-wider text-right">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-[#e3dbcf]">
                                    {campaigns.length === 0 ? (
                                        <tr>
                                            <td colSpan={4} className="px-6 py-12 text-center text-[#8c8578]">
                                                No campaigns found. Create one to get started.
                                            </td>
                                        </tr>
                                    ) : (
                                        campaigns.map((camp) => (
                                            <tr key={camp.name} className="hover:bg-[#f3ede3]/50 transition-colors">
                                                <td className="px-6 py-4">
                                                    <code className="text-sm font-semibold text-[#1a1a1a] bg-[#e3dbcf]/30 px-2 py-1 rounded">
                                                        {camp.name}
                                                    </code>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <div className="text-sm font-medium text-[#1a1a1a]">
                                                        {clients.find(c => c.id === camp.client_id)?.name || `Client #${camp.client_id}`}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <div className="text-sm text-[#6b635a]">
                                                        {new Date(camp.created_at).toLocaleDateString()}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 text-right">
                                                    <div className="flex justify-end gap-2">
                                                        <button
                                                            onClick={() => { setEditingCampaign(camp); setEditName(camp.name); setEditClientId(camp.client_id); setError(null); }}
                                                            className="px-3 py-1.5 bg-[#f3ede3] text-[#1a1a1a] border border-[#e3dbcf] hover:bg-[#e3dbcf] rounded-lg text-xs font-medium transition-all"
                                                        >
                                                            Edit
                                                        </button>
                                                        <button
                                                            onClick={() => handleDeleteCampaign(camp.name)}
                                                            disabled={actionLoading}
                                                            className="px-3 py-1.5 bg-red-50 text-red-600 border border-red-200 hover:bg-red-100 rounded-lg text-xs font-medium transition-all disabled:opacity-50"
                                                        >
                                                            Delete
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {/* Add Modal */}
                {showAddModal && (
                    <div className="fixed inset-0 bg-[#1a1a1a]/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                        <div className="bg-[#fcfbfa] rounded-2xl p-8 max-w-md w-full shadow-2xl border border-[#e3dbcf]">
                            <h3 className="text-2xl font-bold text-[#1a1a1a] mb-2">Create Campaign</h3>
                            <p className="text-[#6b635a] mb-6">Create a new alphanumeric campaign string.</p>

                            {error && (
                                <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
                                    {error}
                                </div>
                            )}

                            <form onSubmit={handleCreateCampaign} className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-[#1a1a1a] mb-1">Company / Client</label>
                                    <select
                                        required
                                        value={newCampaign.client_id}
                                        onChange={(e) => setNewCampaign({ ...newCampaign, client_id: e.target.value })}
                                        className="w-full bg-[#f3ede3] border border-[#e3dbcf] rounded-lg text-[#1a1a1a] px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#1a1a1a]/20 focus:border-[#1a1a1a] transition-all"
                                    >
                                        <option value="">Select a Client</option>
                                        {clients.map(c => (
                                            <option key={c.id} value={c.id}>{c.name}</option>
                                        ))}
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-[#1a1a1a] mb-1">Campaign String</label>
                                    <input
                                        type="text"
                                        required
                                        placeholder="e.g. facebookad1"
                                        value={newCampaign.name}
                                        onChange={(e) => setNewCampaign({ ...newCampaign, name: e.target.value.toLowerCase().replace(/[^a-z0-9]/g, '') })}
                                        className="w-full bg-[#f3ede3] border border-[#e3dbcf] rounded-lg text-[#1a1a1a] px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#1a1a1a]/20 focus:border-[#1a1a1a] transition-all"
                                    />
                                    <p className="text-xs text-[#8c8578] mt-2">Only lowercase alphanumeric characters</p>
                                </div>

                                <div className="flex gap-3 pt-6">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setShowAddModal(false);
                                            setNewCampaign({ name: '', client_id: '' });
                                            setError(null);
                                        }}
                                        disabled={actionLoading}
                                        className="flex-1 px-4 py-3 bg-[#fcfbfa] text-[#1a1a1a] border border-[#e3dbcf] hover:bg-[#f3ede3] rounded-lg font-medium transition-all"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={actionLoading || !newCampaign.name || !newCampaign.client_id}
                                        className="flex-1 px-4 py-3 bg-[#1a1a1a] text-[#f3ede3] hover:bg-[#333] rounded-lg font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {actionLoading ? 'Creating...' : 'Create Campaign'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}
                {/* Edit Modal */}
                {editingCampaign && (
                    <div className="fixed inset-0 bg-[#1a1a1a]/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                        <div className="bg-[#fcfbfa] rounded-2xl p-8 max-w-md w-full shadow-2xl border border-[#e3dbcf]">
                            <h3 className="text-2xl font-bold text-[#1a1a1a] mb-2">Edit Campaign</h3>
                            <p className="text-[#6b635a] mb-6">Editing <code className="bg-[#e3dbcf]/30 px-2 py-0.5 rounded text-[#1a1a1a] font-semibold">{editingCampaign.name}</code></p>

                            {error && (
                                <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
                                    {error}
                                </div>
                            )}

                            <form onSubmit={handleEditCampaign} className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-[#1a1a1a] mb-1">Campaign Name</label>
                                    <input
                                        type="text"
                                        required
                                        value={editName}
                                        onChange={(e) => setEditName(e.target.value.toLowerCase().replace(/[^a-z0-9]/g, ''))}
                                        className="w-full bg-[#f3ede3] border border-[#e3dbcf] rounded-lg text-[#1a1a1a] px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#1a1a1a]/20 focus:border-[#1a1a1a] transition-all"
                                    />
                                    <p className="text-xs text-[#8c8578] mt-1">Only lowercase alphanumeric characters</p>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-[#1a1a1a] mb-1">Assigned Client</label>
                                    <select
                                        required
                                        value={editClientId}
                                        onChange={(e) => setEditClientId(parseInt(e.target.value))}
                                        className="w-full bg-[#f3ede3] border border-[#e3dbcf] rounded-lg text-[#1a1a1a] px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#1a1a1a]/20 focus:border-[#1a1a1a] transition-all"
                                    >
                                        {clients.map(c => (
                                            <option key={c.id} value={c.id}>{c.name}</option>
                                        ))}
                                    </select>
                                    <p className="text-xs text-[#8c8578] mt-1">Reassigning transfers qualified lead costs between client balances</p>
                                </div>

                                <div className="flex gap-3 pt-6">
                                    <button
                                        type="button"
                                        onClick={() => { setEditingCampaign(null); setError(null); }}
                                        disabled={actionLoading}
                                        className="flex-1 px-4 py-3 bg-[#fcfbfa] text-[#1a1a1a] border border-[#e3dbcf] hover:bg-[#f3ede3] rounded-lg font-medium transition-all"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={actionLoading || !editName}
                                        className="flex-1 px-4 py-3 bg-[#1a1a1a] text-[#f3ede3] hover:bg-[#333] rounded-lg font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {actionLoading ? 'Saving...' : 'Save Changes'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}
            </div>
        </AdminLayout>
    );
}
